import React, { useEffect, useState } from 'react';
import { Archive, ArchiveRestore, Trash2, Loader2 } from 'lucide-react';
import {
  listArchivedConversations, archiveConversation, deleteConversation,
  type ConversationSummary,
} from '../../services/conversationService';
import ArchivedChatViewer from './ArchivedChatViewer';
import { useConfirm } from '../../hooks/useConfirm';

/**
 * Archived group chats — the soft-delete bucket. A chat archived from the chat list lands here; the
 * owner can re-open it (read-only history via the normal group route), unarchive it back to the
 * active list, or delete it permanently. Hidden entirely when there's nothing archived.
 */
const ArchivedChats: React.FC = () => {
  const [chats, setChats] = useState<ConversationSummary[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [viewing, setViewing] = useState<ConversationSummary | null>(null);
  const [confirm, confirmDialog] = useConfirm();

  useEffect(() => {
    let alive = true;
    listArchivedConversations().then((c) => { if (alive) setChats(c); }).catch(() => { if (alive) setChats([]); });
    return () => { alive = false; };
  }, []);

  const unarchive = async (id: string) => {
    setBusy(id);
    try { await archiveConversation(id, false); setChats((c) => (c || []).filter((x) => x.id !== id)); }
    catch { /* keep it listed on failure */ } finally { setBusy(null); }
  };
  const remove = async (id: string) => {
    if (!(await confirm({
      title: 'Delete chat permanently?',
      description: 'This chat and its messages will be permanently deleted. This cannot be undone.',
      confirmText: 'Delete',
      variant: 'destructive',
    }))) return;
    setBusy(id);
    try { await deleteConversation(id); setChats((c) => (c || []).filter((x) => x.id !== id)); }
    catch { /* keep it listed on failure */ } finally { setBusy(null); }
  };

  if (!chats || chats.length === 0) return null; // nothing archived → no section

  return (
    <section className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-secondary-100 dark:bg-secondary-700">
          <Archive className="w-5 h-5 text-secondary-700 dark:text-secondary-200" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">Archived chats</h3>
          <p className="text-xs text-secondary-500 dark:text-secondary-400">Reopen, restore, or delete permanently</p>
        </div>
      </div>
      <ul className="space-y-1.5">
        {chats.map((c) => (
          <li key={c.id} className="flex items-center gap-2 rounded-lg border border-secondary-200 dark:border-secondary-700 px-3 py-2">
            <span className="flex-1 truncate text-sm text-secondary-800 dark:text-secondary-100">{c.title || 'Untitled chat'}</span>
            <button onClick={() => setViewing(c)} className="text-xs font-medium text-secondary-600 dark:text-secondary-300 hover:text-secondary-900 dark:hover:text-white px-2 py-1">Open</button>
            <button onClick={() => unarchive(c.id)} disabled={busy === c.id} title="Unarchive" aria-label="Unarchive"
              className="p-1 rounded-md text-secondary-400 hover:text-secondary-700 dark:hover:text-white disabled:opacity-50">
              {busy === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArchiveRestore className="w-4 h-4" />}
            </button>
            <button onClick={() => remove(c.id)} disabled={busy === c.id} title="Delete permanently" aria-label="Delete permanently"
              className="p-1 rounded-md text-secondary-400 hover:text-secondary-500 disabled:opacity-50">
              <Trash2 className="w-4 h-4" />
            </button>
          </li>
        ))}
      </ul>
      {viewing && (
        <ArchivedChatViewer conversationId={viewing.id} title={viewing.title || 'Untitled chat'} onClose={() => setViewing(null)} />
      )}
      {confirmDialog}
    </section>
  );
};

export default ArchivedChats;
